import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import {
  Button,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core'
import { EditableSelect } from '../common/EditableComponents'
import { CustomTooltip } from '../common/Tooltip'
import ModalDialog from '../common/ModalDialog'
import {
  WA_CCRS_ENVIRONMENTS,
  getWaCcrsProviderErrorMessage,
  validateWaCcrsForm,
} from '../../utils/waCcrsCompliance'

const emptyForm = (shop = {}) => ({
  shopId: shop.id,
  companyId: shop.companyId,
  complianceType: 'WA_CCRS',
  stateCode: 'WA',
  environment: 'UAT',
  waCcrsClientIdConfigured: false,
  waCcrsClientSecretConfigured: false,
  enabled: false,
  enableSync: false,
  enableDelivery: false,
})

const mask = (configured, last4) => (configured ? `••••${last4 || ''}` : 'Not configured')

const WaCcrsAccount = ({
  account = {},
  shop = {},
  onSave,
  onDelete,
  onTest,
  notifySuccess = () => {},
  notifyError = () => {},
}) => {
  const [form, setForm] = useState({ ...emptyForm(shop), ...account })
  const [persistedAccount, setPersistedAccount] = useState(
    Object.keys(account).length ? account : null,
  )
  const [credentials, setCredentials] = useState({ clientId: '', clientSecret: '' })
  const [errors, setErrors] = useState({})
  const [isEditing, setIsEditing] = useState(!account.id)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [isClient, setIsClient] = useState(false)
  const deleteAccountModal = useRef()
  const isDeleting = useRef(false)
  const isMounted = useRef(true)

  useEffect(() => {
    setIsClient(true)
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    setForm({ ...emptyForm(shop), ...account })
    setPersistedAccount(Object.keys(account).length ? account : null)
    setCredentials({ clientId: '', clientSecret: '' })
    setErrors({})
    setIsEditing(!account.id)
  }, [account, shop.id])

  const update = (key, value) => {
    setForm((current) => {
      const next = { ...current, [key]: value }
      if (key === 'enabled' && !value) {
        next.enableSync = false
        next.enableDelivery = false
      }
      return next
    })
    setErrors({})
  }

  const save = () => {
    const nextErrors = validateWaCcrsForm(form, credentials, persistedAccount)
    if (Object.keys(nextErrors).length) return setErrors(nextErrors)
    setIsSaving(true)
    return onSave(form, credentials)
      .then((saved) => {
        if (isMounted.current) {
          setForm({ ...emptyForm(shop), ...saved })
          setPersistedAccount(saved)
          setCredentials({ clientId: '', clientSecret: '' })
          setIsEditing(false)
        }
        notifySuccess('WA CCRS configuration saved.')
      })
      .catch((error) => notifyError(getWaCcrsProviderErrorMessage(error)))
      .finally(() => {
        if (isMounted.current) setIsSaving(false)
      })
  }

  const testConnection = () => {
    const nextErrors = validateWaCcrsForm(form, credentials, persistedAccount)
    if (Object.keys(nextErrors).length) return setErrors(nextErrors)
    setIsTesting(true)
    return onTest(form, credentials)
      .then((result) => {
        if (result && result.success === false)
          return notifyError(getWaCcrsProviderErrorMessage(result))
        notifySuccess('WA CCRS connection verified. Configuration was not changed.')
      })
      .catch((error) => notifyError(getWaCcrsProviderErrorMessage(error)))
      .finally(() => {
        if (isMounted.current) setIsTesting(false)
      })
  }

  const cancel = () => {
    setForm({ ...emptyForm(shop), ...account })
    setCredentials({ clientId: '', clientSecret: '' })
    setErrors({})
    setIsEditing(!account.id)
  }

  const deleteAccount = () => deleteAccountModal.current.handleClickOpen()

  const confirmDeleteAccount = () => {
    if (!form.id || isDeleting.current) return Promise.resolve()
    isDeleting.current = true
    deleteAccountModal.current.handleClose()
    setIsSaving(true)
    return onDelete(form.id)
      .then(() => notifySuccess('WA CCRS compliance account deleted.'))
      .catch(() => notifyError('WA CCRS could not delete this compliance account.'))
      .finally(() => {
        isDeleting.current = false
        if (isMounted.current) setIsSaving(false)
      })
  }

  const credentialField = (key, label, configured, last4) =>
    isEditing ? (
      <Grid container alignItems="center" spacing={8} wrap="nowrap">
        <Grid item xs>
          <TextField
            type="password"
            fullWidth
            value={credentials[key]}
            onChange={(event) => {
              setCredentials({ ...credentials, [key]: event.target.value })
              setErrors({})
            }}
            inputProps={{ 'aria-label': label }}
          />
        </Grid>
        {configured ? (
          <Grid item>
            <CustomTooltip
              message={`Leave blank to keep the saved value (${mask(configured, last4)}).`}
            />
          </Grid>
        ) : null}
      </Grid>
    ) : (
      mask(configured, last4)
    )

  return (
    <Paper className="paper-page">
      <div className="table-scroll">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Shop</TableCell>
              <TableCell>Environment</TableCell>
              <TableCell>Client ID</TableCell>
              <TableCell>Client Secret</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Enable Sync</TableCell>
              <TableCell>Enable Delivery</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{shop.name || ''}</TableCell>
              <TableCell>
                <EditableSelect
                  valueToRender={form.environment}
                  editMode={isEditing}
                  value={form.environment}
                  onChange={(event) => update('environment', event.target.value)}
                  inputProps={{ 'aria-label': 'Environment' }}
                  className=""
                >
                  {WA_CCRS_ENVIRONMENTS.map((environment) => (
                    <MenuItem key={environment} value={environment}>
                      {environment}
                    </MenuItem>
                  ))}
                </EditableSelect>
                {errors.environment ? <div role="alert">{errors.environment}</div> : null}
              </TableCell>
              <TableCell>
                {credentialField(
                  'clientId',
                  'Client ID',
                  form.waCcrsClientIdConfigured,
                  form.waCcrsClientIdLast4,
                )}
              </TableCell>
              <TableCell>
                {credentialField(
                  'clientSecret',
                  'Client Secret',
                  form.waCcrsClientSecretConfigured,
                  form.waCcrsClientSecretLast4,
                )}
              </TableCell>
              <TableCell>
                <Switch
                  checked={Boolean(form.enabled)}
                  disabled={!isEditing}
                  onChange={(event) => update('enabled', event.target.checked)}
                  inputProps={{ 'aria-label': 'Enabled' }}
                />
              </TableCell>
              <TableCell>
                <Switch
                  checked={Boolean(form.enableSync)}
                  disabled={!isEditing || !form.enabled}
                  onChange={(event) => update('enableSync', event.target.checked)}
                  inputProps={{ 'aria-label': 'Enable Sync' }}
                />
              </TableCell>
              <TableCell>
                <Switch
                  checked={Boolean(form.enableDelivery)}
                  disabled={!isEditing || !form.enabled}
                  onChange={(event) => update('enableDelivery', event.target.checked)}
                  inputProps={{ 'aria-label': 'Enable Delivery' }}
                />
              </TableCell>
              <TableCell>
                {isEditing ? (
                  <Grid container spacing={8}>
                    <Grid item>
                      <Button
                        className="primary-button"
                        disabled={isSaving || isTesting}
                        onClick={testConnection}
                      >
                        {isTesting ? 'Validating…' : 'Validate'}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        className="primary-button"
                        disabled={isSaving || isTesting}
                        onClick={save}
                      >
                        {isSaving ? 'Saving…' : 'Save'}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        className="danger-button"
                        disabled={isSaving || isTesting}
                        onClick={cancel}
                      >
                        Cancel
                      </Button>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={8}>
                    <Grid item>
                      <Button
                        className="primary-button"
                        disabled={isTesting}
                        onClick={testConnection}
                      >
                        {isTesting ? 'Validating…' : 'Validate'}
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button className="primary-button" onClick={() => setIsEditing(true)}>
                        Edit
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        className="danger-button"
                        disabled={isSaving || !form.id}
                        onClick={deleteAccount}
                      >
                        Delete
                      </Button>
                    </Grid>
                  </Grid>
                )}
                {errors.credentials ? <div role="alert">{errors.credentials}</div> : null}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      {isClient
        ? ReactDOM.createPortal(
            <ModalDialog
              ref={deleteAccountModal}
              title="Delete WA CCRS Account"
              confirmText="Delete"
              onConfirm={confirmDeleteAccount}
            >
              Delete this WA CCRS compliance account?
            </ModalDialog>,
            document.body,
          )
        : null}
    </Paper>
  )
}

export default WaCcrsAccount



// WEBPACK FOOTER //
// ./src/components/Compliance/WaCcrsAccount.js